import { useEffect, useRef, useCallback } from "react";
import { useIsMobile } from "@/hooks/use-mobile";

interface LightningStrikeProps {
  active: boolean;
  originRect?: DOMRect | null;
  onImpact?: () => void;
  direction?: "ltr" | "rtl";
}

const LIGHTNING_COOLDOWN_KEY = "perform_lightning_cooldown";
const POST_REFRESH_KEY = "perform_post_refresh_fadein";

export function markPostRefresh() {
  sessionStorage.setItem(POST_REFRESH_KEY, "1");
}

export function shouldShowPostRefresh(): boolean {
  const v = sessionStorage.getItem(POST_REFRESH_KEY);
  if (v) {
    sessionStorage.removeItem(POST_REFRESH_KEY);
    return true;
  }
  return false;
}

export function getCooldownRemaining(): number {
  const ts = localStorage.getItem(LIGHTNING_COOLDOWN_KEY);
  if (!ts) return 0;
  const diff = 10000 - (Date.now() - Number(ts));
  return diff > 0 ? diff : 0;
}

export function setCooldownNow() {
  localStorage.setItem(LIGHTNING_COOLDOWN_KEY, String(Date.now()));
}

// Generate a jagged lightning bolt path with organic randomness
function generateBolt(
  x1: number, y1: number,
  x2: number, y2: number,
  segments: number,
  jitter: number
): Array<[number, number]> {
  const points: Array<[number, number]> = [[x1, y1]];
  const dx = x2 - x1;
  const dy = y2 - y1;

  for (let i = 1; i < segments; i++) {
    const t = i / segments;
    const mx = x1 + dx * t + (Math.random() - 0.5) * jitter;
    const my = y1 + dy * t + (Math.random() - 0.5) * jitter * 0.3;
    points.push([mx, my]);
  }
  points.push([x2, y2]);
  return points;
}

function drawBolt(
  ctx: CanvasRenderingContext2D,
  points: Array<[number, number]>,
  width: number,
  color: string,
  glow: number
) {
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.shadowColor = color;
  ctx.shadowBlur = glow;
  ctx.beginPath();
  ctx.moveTo(points[0][0], points[0][1]);
  for (let i = 1; i < points.length; i++) {
    ctx.lineTo(points[i][0], points[i][1]);
  }
  ctx.stroke();
  ctx.restore();
}

const LightningStrike = ({ active, originRect, onImpact, direction = "ltr" }: LightningStrikeProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isMobile = useIsMobile();
  const prefersReduced = typeof window !== "undefined"
    && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const impactFired = useRef(false);

  const animate = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const W = window.innerWidth;
    const H = window.innerHeight;
    canvas.width = W * window.devicePixelRatio;
    canvas.height = H * window.devicePixelRatio;
    canvas.style.width = W + "px";
    canvas.style.height = H + "px";
    ctx.scale(window.devicePixelRatio, window.devicePixelRatio);

    const midY = H / 2;

    // Start from outside the screen so we don't see the origin
    const ox = direction === "ltr" ? -120 : W + 120;
    const tx = direction === "ltr" ? W + 120 : -120;
    const oy = midY + (Math.random() - 0.5) * 40;
    const ty = midY + (Math.random() - 0.5) * 40;

    const duration = isMobile ? 500 : 800;
    const start = performance.now();

    // Pre-generate all bolts (main + branches) for consistent animation
    const jitter = Math.min(W, H) * 0.18;
    const mainBolt = generateBolt(ox, oy, tx, ty, isMobile ? 10 : 16, jitter);

    // Generate 3-5 branches from random points on the main bolt
    const branchCount = isMobile ? 2 : 4;
    const branches: Array<{ points: Array<[number, number]>; width: number; opacity: number }> = [];
    for (let b = 0; b < branchCount; b++) {
      const srcIdx = Math.floor(2 + Math.random() * (mainBolt.length - 4));
      const src = mainBolt[srcIdx];
      const angle = (Math.random() - 0.5) * 1.8;
      const len = 40 + Math.random() * (isMobile ? 60 : 120);
      const endX = src[0] + Math.cos(angle) * len;
      const endY = src[1] + Math.abs(Math.sin(angle)) * len + 20;
      const brPts = generateBolt(src[0], src[1], endX, endY, isMobile ? 4 : 6, jitter * 0.5);
      branches.push({ points: brPts, width: 0.8 + Math.random() * 1.2, opacity: 0.4 + Math.random() * 0.4 });
    }

    // Sub-branches off branches
    const subBranches: typeof branches = [];
    if (!isMobile) {
      for (const br of branches) {
        if (Math.random() > 0.5 && br.points.length > 2) {
          const si = Math.floor(1 + Math.random() * (br.points.length - 2));
          const sp = br.points[si];
          const sa = (Math.random() - 0.5) * 2;
          const sl = 20 + Math.random() * 50;
          const sbPts = generateBolt(sp[0], sp[1], sp[0] + Math.cos(sa) * sl, sp[1] + Math.abs(Math.sin(sa)) * sl + 10, 3, jitter * 0.3);
          subBranches.push({ points: sbPts, width: 0.5, opacity: 0.3 });
        }
      }
    }

    const primaryColor = "rgb(206, 220, 71)"; // Primary green
    const coreColor = "rgb(230, 245, 120)"; // Bright lime core
    const branchColor = "rgb(180, 200, 60)"; // Darker green branches

    let frame: number;

    const render = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      ctx.clearRect(0, 0, W, H);

      // Fade: ramp up fast, hold, then fade
      let alpha: number;
      if (progress < 0.15) {
        alpha = progress / 0.15;
      } else if (progress < 0.6) {
        alpha = 1;
        // Flicker effect
        if (Math.random() > 0.7) alpha = 0.7 + Math.random() * 0.3;
      } else {
        alpha = 1 - (progress - 0.6) / 0.4;
      }
      ctx.globalAlpha = Math.max(0, alpha);

      // Draw visible portion based on progress
      const drawProgress = Math.min(progress / 0.3, 1); // bolt draws in first 30%
      const visibleMain = mainBolt.slice(0, Math.max(2, Math.ceil(mainBolt.length * drawProgress)));

      // Outer glow
      drawBolt(ctx, visibleMain, isMobile ? 5 : 8, primaryColor, isMobile ? 20 : 40);
      // Main bolt
      drawBolt(ctx, visibleMain, isMobile ? 2.5 : 4, primaryColor, isMobile ? 10 : 20);
      // White-hot core
      drawBolt(ctx, visibleMain, isMobile ? 1 : 2, coreColor, 8);

      // Branches appear after main bolt draws
      if (drawProgress >= 0.5) {
        const brAlpha = Math.min((drawProgress - 0.5) / 0.5, 1);
        ctx.globalAlpha = Math.max(0, alpha * brAlpha);
        for (const br of branches) {
          drawBolt(ctx, br.points, br.width * 2, branchColor, 15);
          drawBolt(ctx, br.points, br.width, coreColor, 5);
        }
        for (const sb of subBranches) {
          drawBolt(ctx, sb.points, sb.width, branchColor, 10);
        }
      }

      // Flash at impact moment (30-50% of timeline)
      if (progress > 0.25 && progress < 0.45) {
        const flashIntensity = 1 - Math.abs(progress - 0.35) / 0.1;
        ctx.globalAlpha = Math.max(0, flashIntensity * 0.3);
        const grad = ctx.createRadialGradient(tx, ty, 0, tx, ty, H * 0.5);
        grad.addColorStop(0, "rgba(160, 180, 255, 1)");
        grad.addColorStop(0.3, "rgba(140, 160, 255, 0.4)");
        grad.addColorStop(1, "transparent");
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, W, H);

        // Fire impact callback once at flash peak — clear canvas first
        if (!impactFired.current && progress > 0.35) {
          impactFired.current = true;
          cancelAnimationFrame(frame);
          ctx.clearRect(0, 0, W, H);
          // Brief white flash then reload
          canvas.style.background = "hsl(var(--background))";
          canvas.style.opacity = "1";
          setTimeout(() => onImpact?.(), 60);
          return; // stop rendering
        }
      }

      if (progress < 1) {
        frame = requestAnimationFrame(render);
      }
    };

    frame = requestAnimationFrame(render);
    return () => cancelAnimationFrame(frame);
  }, [direction, isMobile, onImpact]);

  useEffect(() => {
    if (!active) {
      impactFired.current = false;
      return;
    }

    if (prefersReduced) {
      const t = setTimeout(() => onImpact?.(), 100);
      return () => clearTimeout(t);
    }

    return animate();
  }, [active, animate, prefersReduced, onImpact]);

  if (!active || prefersReduced) return null;

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 z-[9999] pointer-events-none"
      aria-hidden="true"
    />
  );
};

export default LightningStrike;
